import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { usePathname, useSearchParams } from 'next/navigation';

import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { Button, Icon, Typography } from '@/components/common';
import { Input } from '@/components/form';
import { Combobox, Options } from '@/components/ui/combobox';
import { ItemInboxCase } from '@/interfaces/inbox';
import Icons from '@/const/icons';

interface FiltersProps {
	userCases: ItemInboxCase[];
	total: number;
	totalItems: number;
	isSearch: boolean;
	setOpenNewMessage: (value: boolean) => void;
}

export const Filters: React.FC<FiltersProps> = ({
	setOpenNewMessage,
	isSearch,
	total,
	totalItems,
	userCases,
}) => {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const params = new URLSearchParams(searchParams);

	const { replace } = useRouter();
	const { register } = useForm();
	const [open, setOpen] = useState(false);
	const [filterSelected, setFilterSelected] = useState<string | undefined>();
	const [filterOptions, setFilterOptions] = useState<Options[]>([]);

	const handleChange = (id: string | undefined): void => {
		if (id === 'clear-all') {
			params.delete('caseId');
			setFilterSelected(undefined);
			replace(`${pathname}`);
		} else {
			setFilterSelected(id);
			if (id) {
				params.set('caseId', id);
			} else {
				params.delete('caseId');
			}
			replace(`${pathname}?${params.toString()}`);
		}
	};

	const handleSearch = useDebouncedCallback((value: string) => {
		params.set('page', '1');
		if (value) {
			params.set('search', value);
		} else {
			params.delete('search');
		}
		replace(`${pathname}?${params.toString()}`);
	}, 300);

	useEffect(() => {
		const parsedCases: Options[] = [];

		if (filterSelected) {
			parsedCases.push({
				label: 'Clear all',
				value: 'clear-all',
				icon: (
					<Icon
						src={Icons.clear}
						className="text-primary"
						width={24}
						height={24}
					/>
				),
			});
		}

		userCases.map((item) => {
			parsedCases.push({
				value: item.case.caseId,
				label: item.case.caseName,
			});
		});
		setFilterOptions(parsedCases);
	}, [userCases, filterSelected]);

	return (
		<div className="flex items-center justify-between">
			<div className="flex-1 absolute bottom-5 right-4 md:static">
				<Button
					size="medium"
					decoration="secondary"
					icon={Icons.arrowLeft}
					className="!py-4 !px-4 !w-44 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.32)] md:shadow-none"
					onClick={() => setOpenNewMessage(true)}
				>
					<Typography
						type="semibold-16"
						className="flex items-center justify-center gap-3 w-full"
					>
						<Icon src={Icons.edit} className="!w-4 !h-4" />
						New Message
					</Typography>
				</Button>
			</div>
			<div className="flex justify-between md:justify-end items-center gap-1 w-full">
				<div className="flex-1 md:flex-none max-">
					<Input
						name="search"
						register={register}
						placeholder="Search"
						iconLeft={Icons.search}
						classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
						classNameInput="pl-9 xl:w-72 !bg-gray-15 !h-[33px] !m-0 border-none"
						onChange={({ target }) => handleSearch(target.value)}
					/>
				</div>
				<div>
					<Combobox
						open={open}
						placeholder="Cases"
						setOpen={setOpen}
						options={filterOptions}
						value={filterSelected}
						classNameTrigger={clsx('h-8 max-w-min', {
							'min-w-36': !!filterSelected,
						})}
						classNameButton={'!min-w-min'}
						setValue={(value) => handleChange(value)}
					/>
				</div>
				<div>
					<Typography type="regular-12" className="opacity-60 hidden md:block">
						{isSearch
							? `Showing ${total} search results`
							: `Showing ${total} of ${totalItems} emails`}
					</Typography>
				</div>
			</div>
		</div>
	);
};
